import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { PeekButton, findPeekButton, spaceToPeek } from '../components/ui/Peek';
import LogoSpinner from '../components/ui/LogoSpinner';
import Panel, { StatTile, type StatTone } from '../components/ui/Panel';
import EmptyState from '../components/ui/EmptyState';
import { STATE } from '../components/ui/motion';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import SearchField from '../components/ui/SearchField';
import SegTrack from '../components/ui/SegTrack';
import { CATEGORY } from '../components/ui/categoryColors';
import { RelationTag } from '../Sales/StatusPill';
import CustomerPeek from './CustomerPeek';
import { Customer, FilterType, getStatusBadges } from './types';

// One 3px stripe per relation, stacked from the left edge: lease, rental, purchase.
const STRIPES = [
  { flag: 'is_lessee', hue: 'blue' },
  { flag: 'is_renter', hue: 'orange' },
  { flag: 'is_purchaser', hue: 'green' },
] as const;

const relationEdge = (c: Customer): string | undefined => {
  const held = STRIPES.filter(s => c[s.flag]);
  return held.length
    ? held.map((s, i) => `inset ${(i + 1) * 3}px 0 0 ${CATEGORY[s.hue].mark.oklch}`).join(', ')
    : undefined;
};

const CustomerList: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('');
  const [peekId, setPeekId] = useState<number | null>(null);
  const peekReturn = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const data = await api.get('/customers');
        setCustomers(data || []);
      } catch (error) {
        toast.error('Failed to load customers');
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = customers.filter(c => {
    const matchesSearch = !search ||
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.customer_code?.toLowerCase().includes(search.toLowerCase()) ||
      c.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.shipping_address?.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = !filterType ||
      (filterType === 'lessee' && c.is_lessee) ||
      (filterType === 'renter' && c.is_renter) ||
      (filterType === 'purchaser' && c.is_purchaser);
    return matchesSearch && matchesFilter;
  });

  const openPeek = (c: Customer, from: HTMLElement) => {
    peekReturn.current = from;
    setPeekId(c.id);
  };
  const peekCustomer = peekId == null ? null : customers.find(c => c.id === peekId) || null;
  const peekIndex = peekId == null ? -1 : filtered.findIndex(c => c.id === peekId);
  // Steps in the order the cards are laid out, and hands focus back to the shown card's key.
  const stepPeek = (dir: 1 | -1) => {
    if (peekId == null || filtered.length === 0) return;
    const next = filtered[((peekIndex < 0 ? 0 : peekIndex + dir) + filtered.length) % filtered.length];
    const key = findPeekButton(next.id);
    if (key) peekReturn.current = key;
    setPeekId(next.id);
  };

  const leaseCount = customers.filter(c => c.is_lessee).length;
  const rentalCount = customers.filter(c => c.is_renter).length;
  const purchaseCount = customers.filter(c => c.is_purchaser).length;

  if (loading) return <LogoSpinner label="Loading customers" />;

  return (
    <PageLayout kind="workspace" className="pt-4">
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-primary">Customers</h1>
          <p className="text-on-surface-variant text-sm mt-0.5">{customers.length} customers</p>
        </div>
        <Link
          to="/customers/new"
          className={`${buttonClass('filled', 'md')} self-start sm:self-auto`}
        >
          + New Customer
        </Link>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Customers', value: customers.length, tone: 'neutral' },
          { label: 'Lease', value: leaseCount, tone: 'blue' },
          { label: 'Rental', value: rentalCount, tone: 'orange' },
          { label: 'Purchase', value: purchaseCount, tone: 'green' },
        ].map(stat => (
          <StatTile key={stat.label} label={stat.label} value={stat.value} tone={stat.tone as StatTone} />
        ))}
      </div>

      {/* Search + filter */}
      <div className="flex gap-3 flex-wrap">
        <SearchField
          label="Search customers"
          value={search}
          onChange={setSearch}
          placeholder="Search customers..."
          className="flex-1 min-w-[200px]"
        />
        <SegTrack
          label="Customer type"
          value={filterType || 'all'}
          onChange={key => setFilterType(key === 'all' ? '' : key as FilterType)}
          options={[
            { key: 'all', label: 'All' },
            { key: 'lessee', label: 'Lease' },
            { key: 'renter', label: 'Rental' },
            { key: 'purchaser', label: 'Purchase' },
          ]}
        />
      </div>

      {/* Customer grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(customer => (
          // The name link stretches over the whole card; the peek key sits above it as a sibling,
          // never inside it.
          <Panel
            key={customer.id}
            label={customer.name}
            className={`group relative overflow-hidden ${STATE}`}
          >
            <span aria-hidden="true" data-relation-edge className="pointer-events-none absolute inset-0" style={{ boxShadow: relationEdge(customer) }} />
            {/* Company name + badges */}
            <div className="flex items-start justify-between gap-2 mb-3">
              <Link
                to={`/customers/${customer.id}`}
                onKeyDown={spaceToPeek((from) => openPeek(customer, from))}
                className="min-w-0 focus:outline-none after:absolute after:inset-0 after:rounded-md after:content-[''] focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-primary/50"
              >
                <h3 className="text-white font-semibold text-base leading-tight group-hover:text-primary transition-colors duration-150">
                  {customer.name}
                </h3>
                {customer.customer_code && (
                  <div className="text-[11px] text-on-surface-variant font-mono tracking-wider mt-0.5">{customer.customer_code}</div>
                )}
              </Link>
              <div className="flex flex-shrink-0 items-center gap-1">
                {getStatusBadges(customer).map(badge => (
                  <RelationTag key={badge} badge={badge} size="sm" />
                ))}
                <PeekButton name={customer.name} peekFor={customer.id} onPeek={(from) => openPeek(customer, from)} className="-my-1 -mr-2" />
              </div>
            </div>

            {/* Contact */}
            {customer.contact_name && (
              <div className="text-on-surface-variant text-sm mb-1">{customer.contact_name}</div>
            )}
            {customer.phone && (
              <div className="text-on-surface-variant text-xs mb-3 font-mono">{customer.phone}</div>
            )}

            {/* Customer type indicators */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-auto pt-2 border-t border-gray-700/40">
              {!customer.is_lessee && !customer.is_renter && !customer.is_purchaser && (
                <span className="text-on-surface-disabled italic">No type assigned</span>
              )}
            </div>

            {/* Notes preview */}
            {customer.notes && (
              <div className="text-on-surface-variant text-xs mt-2 line-clamp-2 leading-relaxed">
                {customer.notes}
              </div>
            )}
          </Panel>
        ))}

        {filtered.length === 0 && (
          <EmptyState className="col-span-full">
            {search || filterType ? 'No customers match your search' : 'No customers yet'}
          </EmptyState>
        )}
      </div>

      <CustomerPeek
        customer={peekCustomer}
        onClose={() => setPeekId(null)}
        returnTo={peekReturn}
        step={peekIndex >= 0 ? { index: peekIndex, total: filtered.length, onStep: stepPeek, noun: 'customer' } : undefined}
      />
    </div>
    </PageLayout>
  );
};

export default CustomerList;
